import { useRef } from 'react'

import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useMutation } from 'react-query'

import { useCompany } from 'contexts'
import { SPARCK_COMPANY } from 'constants/app'
import { API } from 'core/requests'

export default function SettingsCompanyLogo() {
  const { enqueueSnackbar } = useSnackbar()
  const { company, setCompany } = useCompany()!
  const fileInputRef = useRef<HTMLInputElement>(null)

  const applyCompany = (response: any) => {
    if (response?.company?.id) {
      setCompany(response.company)
      localStorage.setItem(SPARCK_COMPANY, JSON.stringify(response.company))
    }
  }

  // The shared POST /v1/uploads endpoint returns the company attributes flat,
  // with the freshly attached file under `logo: { url }` — not wrapped in a
  // `company` envelope like CompanyAdapter. Merge the new url into the company
  // already in context so the sidebar/header update without a reload.
  const applyLogo = (response: any) => {
    const url = response?.logo?.url
    if (!url || !company) return
    const updated = { ...company, logo: { url } }
    setCompany(updated)
    localStorage.setItem(SPARCK_COMPANY, JSON.stringify(updated))
  }

  const { mutate: uploadLogo, isLoading: uploading } = useMutation(
    (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('attach_to', 'Company')
      formData.append('record_id', String(company?.id))
      formData.append('record_field', 'logo')

      return API.base('uploads', {
        method: 'POST',
        body: formData,
      })
    },
    {
      onSuccess: (response: any) => {
        if (response?.errors) return
        applyLogo(response)
        enqueueSnackbar('Logo updated.', { variant: 'success' })
      },
    },
  )

  const { mutate: removeLogo, isLoading: removing } = useMutation(
    () =>
      API.base('removeCompanyLogo', {
        method: 'DELETE',
        pathParams: { id: company?.id },
      }),
    {
      onSuccess: (response: any) => {
        if (response?.errors) return
        applyCompany(response)
        enqueueSnackbar('Logo removed.', { variant: 'success' })
      },
    },
  )

  const logoUrl = company?.logo?.url

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) uploadLogo(file)
    event.target.value = '' // allow re-selecting the same file
  }

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item>
        {logoUrl ? (
          <Box
            component='img'
            src={logoUrl}
            alt='Company logo'
            sx={{
              maxHeight: 64,
              maxWidth: 220,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No logo uploaded — the Sparck logo is shown.
          </Typography>
        )}
      </Grid>

      <Grid item>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/png,image/jpeg,image/gif,image/webp'
          hidden
          onChange={onFileChange}
        />
        <Button
          variant='outlined'
          color='primary'
          disabled={uploading || removing}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? <CircularProgress size={20} /> : 'Upload Logo'}
        </Button>

        {logoUrl && (
          <Button
            color='error'
            sx={{ ml: 1 }}
            disabled={uploading || removing}
            onClick={() => removeLogo()}
          >
            {removing ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography variant='caption' display='block' color='text.secondary'>
          Upload your organization's logo to replace the Sparck logo in the sidebar
          and header. A &quot;Powered by Sparck&quot; note is shown beneath it. Use a
          PNG or JPG with a transparent background (SVG is not supported).
        </Typography>
      </Grid>
    </Grid>
  )
}
